import {
  Target,
  DollarSign,
  Settings,
  Megaphone,
  Banknote,
  Shield,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Building2,
  Calendar,
  ClipboardList,
  ShieldCheck,
  FileText,
} from 'lucide-react';

export const API = process.env.REACT_APP_BACKEND_URL;

// Leadership roles that can send suggestions
export const LEADERSHIP_ROLES = ['ceo', 'president', 'president_&_ceo', 'director_of_sales', 'general_manager', 'leadership', 'director', 'vp'];

// Icon mapping
export const ICON_MAP = {
  'target': Target,
  'dollar-sign': DollarSign,
  'settings': Settings,
  'megaphone': Megaphone,
  'banknote': Banknote,
  'shield': Shield,
  'building': Building2,
  'calendar': Calendar,
  'clipboard-list': ClipboardList,
  'shield-check': ShieldCheck,
  'file-text': FileText,
};

// Color mapping
export const COLOR_MAP = {
  green: { bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-600 dark:text-green-400' },
  blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-600 dark:text-blue-400' },
  purple: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-600 dark:text-purple-400' },
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-600 dark:text-emerald-400' },
  slate: { bg: 'bg-slate-500/10', border: 'border-slate-500/30', text: 'text-slate-600 dark:text-slate-400' },
  amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-600 dark:text-amber-400' },
  red: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-600 dark:text-red-400' },
  indigo: { bg: 'bg-indigo-500/10', border: 'border-indigo-500/30', text: 'text-indigo-600 dark:text-indigo-400' },
};

// Status styling
export const STATUS_STYLES = {
  not_started: { label: 'Not Started', color: 'bg-slate-500/10 text-slate-600 border-slate-500/20' },
  planning: { label: 'Planning', color: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  in_progress: { label: 'In Progress', color: 'bg-amber-500/10 text-amber-600 border-amber-500/20' },
  testing: { label: 'Testing', color: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  live: { label: 'Live', color: 'bg-green-500/10 text-green-600 border-green-500/20' },
  on_hold: { label: 'On Hold', color: 'bg-red-500/10 text-red-600 border-red-500/20' },
  completed: { label: 'Completed', color: 'bg-green-500/10 text-green-600 border-green-500/20' },
  pending: { label: 'Pending', color: 'bg-slate-500/10 text-slate-600 border-slate-500/20' },
};

export const HEALTH_STYLES = {
  green: { icon: CheckCircle, color: 'text-green-500', label: 'On Track' },
  yellow: { icon: AlertTriangle, color: 'text-amber-500', label: 'At Risk' },
  red: { icon: XCircle, color: 'text-red-500', label: 'Blocked' },
};

// Phase status colors for dashboard card display
export const PHASE_STATUS_COLORS = {
  planning: { bg: 'bg-slate-500', text: 'text-slate-500', label: 'Planning' },
  design: { bg: 'bg-purple-500', text: 'text-purple-500', label: 'Design' },
  building: { bg: 'bg-blue-400', text: 'text-blue-400', label: 'Building' },
  coding: { bg: 'bg-blue-600', text: 'text-blue-600', label: 'Coding' },
  internal_testing: { bg: 'bg-cyan-500', text: 'text-cyan-500', label: 'Internal Testing' },
  team_testing: { bg: 'bg-teal-500', text: 'text-teal-500', label: 'Team Testing' },
  debug: { bg: 'bg-orange-500', text: 'text-orange-500', label: 'De-Bug' },
  edit_upgrades: { bg: 'bg-amber-500', text: 'text-amber-500', label: 'Edit/Upgrades' },
  final_testing: { bg: 'bg-indigo-500', text: 'text-indigo-500', label: 'Final Testing' },
  release_ready: { bg: 'bg-emerald-500', text: 'text-emerald-500', label: 'Release Ready' },
  needs_attention: { bg: 'bg-red-500', text: 'text-red-500', label: 'Needs Attention' },
  completed: { bg: 'bg-green-500', text: 'text-green-500', label: 'Completed' },
};

// Status options for dropdowns
export const DELIVERABLE_STATUS_OPTIONS = [
  { value: 'planning', label: 'Planning', color: 'bg-slate-500' },
  { value: 'design', label: 'Design', color: 'bg-purple-500' },
  { value: 'building', label: 'Building', color: 'bg-blue-400' },
  { value: 'coding', label: 'Coding', color: 'bg-blue-600' },
  { value: 'internal_testing', label: 'Internal Testing', color: 'bg-cyan-500' },
  { value: 'team_testing', label: 'Team Testing', color: 'bg-teal-500' },
  { value: 'debug', label: 'De-Bug', color: 'bg-orange-500' },
  { value: 'edit_upgrades', label: 'Edit/Upgrades', color: 'bg-amber-500' },
  { value: 'final_testing', label: 'Final Testing', color: 'bg-indigo-500' },
  { value: 'release_ready', label: 'Release Ready', color: 'bg-emerald-500' },
  { value: 'needs_attention', label: 'Needs Attention', color: 'bg-red-500' },
  { value: 'completed', label: 'Completed', color: 'bg-green-500' },
];

// Default phase IDs
export const DEFAULT_PHASE_IDS = ['phase1', 'phase2', 'phase3', 'phase4', 'phase5', 'phase6', 'phase7'];

// Default team members for task assignment
export const DEFAULT_TEAM_MEMBERS = [
  { id: 'ryan_march', name: 'Ryan March', role: 'Lead Developer', avatar: 'RM' },
  { id: 'ryan_adams', name: 'Ryan Adams', role: 'Developer', avatar: 'RA' },
  { id: 'gabe', name: 'Gabriel Gecan', role: 'Developer', avatar: 'GG' },
];
